import React, { createContext, useContext, useState, useEffect } from 'react';
import { userAPI } from '../services/api';
import { useAuth } from './AuthContext';

const SettingsContext = createContext();

// Default settings
const defaultSettings = {
  defaultDaysFilter: 15,
  primaryColor: '#3B82F6',
  secondaryColor: '#10B981',
  sidebarColor: '#1F2937',
  dateFormat: 'DD/MM/YYYY',
  currencySymbol: '₹',
  timeFormat: '12h'
};

// Predefined color themes
export const colorThemes = [
  { name: 'Blue', primary: '#3B82F6', secondary: '#10B981', sidebar: '#1F2937' },
  { name: 'Indigo', primary: '#6366F1', secondary: '#8B5CF6', sidebar: '#1E1B4B' },
  { name: 'Purple', primary: '#9333EA', secondary: '#EC4899', sidebar: '#3B0764' },
  { name: 'Green', primary: '#22C55E', secondary: '#14B8A6', sidebar: '#14532D' },
  { name: 'Orange', primary: '#F97316', secondary: '#EAB308', sidebar: '#431407' },
  { name: 'Red', primary: '#EF4444', secondary: '#F97316', sidebar: '#450A0A' },
  { name: 'Teal', primary: '#14B8A6', secondary: '#06B6D4', sidebar: '#134E4A' },
  { name: 'Rose', primary: '#F43F5E', secondary: '#EC4899', sidebar: '#4C0519' }
];

// Days filter options
export const daysFilterOptions = [
  { value: 7, label: 'Last 7 days' },
  { value: 15, label: 'Last 15 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 60, label: 'Last 60 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 0, label: 'All records' }
];

export const SettingsProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [settings, setSettings] = useState(defaultSettings);
  const [loading, setLoading] = useState(true);

  // Fetch settings when user logs in
  useEffect(() => {
    if (isAuthenticated && user) {
      fetchSettings();
    } else {
      setSettings(defaultSettings);
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  // Apply CSS variables for theme colors
  useEffect(() => {
    document.documentElement.style.setProperty('--primary-color', settings.primaryColor);
    document.documentElement.style.setProperty('--secondary-color', settings.secondaryColor);
    document.documentElement.style.setProperty('--sidebar-color', settings.sidebarColor);
  }, [settings.primaryColor, settings.secondaryColor, settings.sidebarColor]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getAppSettings();
      if (response.data.success && response.data.settings) {
        setSettings({ ...defaultSettings, ...response.data.settings });
      }
    } catch (error) {
      console.error('Error fetching app settings:', error);
      // Use defaults on error
      setSettings(defaultSettings);
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (newSettings) => {
    console.log('updateSettings called with:', newSettings);
    try {
      // Immediately apply settings locally for instant feedback
      setSettings(prev => ({ ...prev, ...newSettings }));

      console.log('Calling API to save settings...');
      const response = await userAPI.updateAppSettings(newSettings);
      console.log('API response:', response.data);

      if (response.data.success) {
        // Update with server response (in case of any transformations)
        if (response.data.settings) {
          setSettings(prev => ({ ...prev, ...response.data.settings }));
        }
        return { success: true };
      }
      // Revert on failure
      console.log('API returned success: false, reverting...');
      await fetchSettings();
      return { success: false, message: response.data.message || 'Failed to update settings' };
    } catch (error) {
      console.error('Error updating settings:', error);
      console.error('Error response:', error.response?.data);
      // Revert on error
      await fetchSettings();
      return { success: false, message: error.response?.data?.message || 'Error updating settings' };
    }
  };

  // Helper to get date range based on defaultDaysFilter
  const getDefaultDateRange = () => {
    if (settings.defaultDaysFilter === 0) {
      return { start: '', end: '' }; // All records
    }

    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - (settings.defaultDaysFilter - 1));

    return {
      start: startDate.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0]
    };
  };

  return (
    <SettingsContext.Provider value={{
      settings,
      loading,
      updateSettings,
      fetchSettings,
      getDefaultDateRange,
      colorThemes,
      daysFilterOptions
    }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export default SettingsContext;
